import { ApiError } from '../lib/apiClient';
import { TFunction } from 'i18next';

/**
 * Extracts a user-friendly error message from various error formats.
 * Handles JSON strings like {"error":"message"} and plain strings.
 * Also handles Zod validation errors with details.fieldErrors
 */
export function parseErrorMessage(error: unknown, fallbackMessage = 'An error occurred'): string {
    if (!error) {
        return fallbackMessage;
    }

    // If it's an Error object, get the message
    let message = error instanceof Error ? error.message : String(error);

    // Try to parse JSON error responses
    try {
        // Check if the message looks like JSON
        if (message.startsWith('{') && message.endsWith('}')) {
            const parsed = JSON.parse(message);

            // Handle Zod validation details (fieldErrors)
            if (parsed.details?.fieldErrors) {
                const fieldErrors = parsed.details.fieldErrors;
                const messages: string[] = [];
                for (const field of Object.keys(fieldErrors)) {
                    const errors = fieldErrors[field];
                    if (Array.isArray(errors) && errors.length > 0) {
                        messages.push(`${field}: ${errors.join(', ')}`);
                    }
                }
                if (messages.length > 0) {
                    return messages.join('; ');
                }
            }

            // Handle common API error formats
            if (typeof parsed.error === 'string') {
                return parsed.error;
            }
            if (typeof parsed.message === 'string') {
                return parsed.message;
            }
            if (typeof parsed.errors === 'string') {
                return parsed.errors;
            }
            // Handle array of errors
            if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
                return parsed.errors.map((e: { message?: string }) => e.message ?? e).join(', ');
            }
        }
    } catch {
        // Not valid JSON, continue with original message
    }

    return message || fallbackMessage;
}

/**
 * Helper to get a translated error message from an API error
 */
export function getApiErrorMessage(error: unknown, t: TFunction, fallbackMessage = 'apiErrors.UNKNOWN_ERROR'): string {
    if (error instanceof ApiError && error.code) {
        const translationKey = `apiErrors.${error.code}`;
        const translated = t(translationKey);
        // If translation differs from key, it exists
        if (translated && translated !== translationKey) {
            return translated;
        }
    }

    // Use parseErrorMessage for robust JSON/Zod handling
    const rawMessage = parseErrorMessage(error, fallbackMessage);

    // Map common error messages to translation keys
    const errorMappings: Record<string, string> = {
        "User not found.": "apiErrors.USER_NOT_FOUND",
        "Tenant not found.": "apiErrors.TENANT_NOT_FOUND",
        "Dealer not found.": "apiErrors.DEALER_NOT_FOUND",
        "Application not found.": "apiErrors.APPLICATION_NOT_FOUND",
        "User belongs to a different dealer.": "apiErrors.USER_DIFFERENT_DEALER",
        "User belongs to a different tenant.": "apiErrors.USER_DIFFERENT_TENANT",
        "Cannot reactivate user: the tenant is inactive. Please reactivate the tenant first.": "apiErrors.TENANT_INACTIVE_REACTIVATION",
        "You cannot delete your own account.": "apiErrors.CANNOT_DELETE_SELF",
        "Dealer administrators cannot create master accounts.": "apiErrors.DEALER_CANNOT_CREATE_MASTER",
        "Tenant administrators can only create users for their own tenant.": "apiErrors.TENANT_ADMIN_OWN_TENANT_ONLY",
        "Invalid username or password": "apiErrors.INVALID_CREDENTIALS",
        "Your dealer license has expired. Please contact support.": "apiErrors.DEALER_LICENSE_EXPIRED",
        "Invalid or expired token": "apiErrors.INVALID_TOKEN",
        "Invalid token": "apiErrors.INVALID_TOKEN",
        "Invalid session": "apiErrors.INVALID_SESSION",
        "Current password is incorrect": "apiErrors.INCORRECT_PASSWORD",
        "Audit logs are restricted to master administrators only.": "apiErrors.AUDIT_MASTER_ONLY"
    };

    // Check if raw message matches a known pattern
    for (const [pattern, translationKey] of Object.entries(errorMappings)) {
        if (rawMessage.includes(pattern)) {
            const translated = t(translationKey);
            if (translated && translated !== translationKey) {
                return translated;
            }
            // If no translation, fall through to show original message
            break;
        }
    }

    // If fallback was a key (e.g. UNKNOWN_ERROR), translate it
    if (rawMessage === fallbackMessage) {
        return t(fallbackMessage);
    }

    return rawMessage;
}
